<script setup lang="ts">
import type { CSSProperties } from 'vue';

import type { ImageGridSection, Theme } from './report-typing';

import { useUpdatedReportStore } from '@src/store/report-store';
import { computed } from 'vue';

import FieldRenderer from './FieldRenderer.vue';
import ReportImageCell from './ReportImageCell.vue';

const props = defineProps<{
  section: ImageGridSection;
}>();

const reportStore = useUpdatedReportStore();

const theme = computed<Theme | null>(() => {
  const loadedTemplate = reportStore.getTemplate();
  return loadedTemplate?.theme ?? null;
});

const titleStyle = computed<CSSProperties>(() => {
  if (!theme.value) return {};
  const sl = theme.value.fonts.sectionLabel;
  return {
    fontFamily: sl.family,
    fontSize: `${sl.size}px`,
    fontWeight: sl.weight,
    color: theme.value.colors.text,
    textTransform: sl.uppercase ? 'uppercase' : 'none',
    letterSpacing: sl.letterSpacing ? `${sl.letterSpacing}px` : undefined,
  };
});

const gridStyle = computed<CSSProperties>(() => {
  const cols = props.section.columns || 2;
  const gap = theme.value ? theme.value.spacing.fieldGap : 8;
  return {
    display: 'grid',
    gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`,
    gap: `${gap}px`,
    alignItems: 'start',
  };
});
</script>

<template>
  <div class="image-grid-section">
    <!-- Title -->
    <div v-if="section.title" class="image-grid-header">
      <span class="image-grid-title" :style="titleStyle">
        {{ section.title }}
      </span>
    </div>

    <!-- Grid -->
    <div class="image-grid" :style="gridStyle">
      <div v-for="cell in section.cells" :key="cell.id" class="image-cell">
        <!-- Cell title (editable) -->
        <FieldRenderer :field="cell.titleField" />

        <!-- Image cell with drag/drop, pan, zoom via Fabric.js -->
        <ReportImageCell :cell="cell" :image-height="section.imageHeight" />
      </div>
    </div>
  </div>
</template>

<style scoped>
.image-grid-section {
  display: flex;
  flex-direction: column;
  gap: 6px;
  width: 100%;
  box-sizing: border-box;
}

.image-grid-header {
  margin-bottom: 2px;
}

.image-grid-title {
  font-size: 11px;
  font-weight: 600;
}

.image-grid {
  width: 100%;
  min-width: 0;
  box-sizing: border-box;
}

.image-cell {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
  width: 100%;
  box-sizing: border-box;
}
</style>
